import { inject, Injectable, Injector, Signal } from '@angular/core';
import { MarketplaceItemStore } from './marketplace-v2.store';
import { Observable } from 'rxjs';
import { MarketplaceItemV2 } from './models/marketplace-item-v2.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProcessProviderId } from '../models/process-provider-id';
import { EntityOptions } from '../../../shared/store/entity-state.feature';

/**
 * Query facade for the v2 marketplace signal store.
 *
 * Exposes store signals and observables for components and services to consume,
 * and provides attribute localisation and item type resolution utilities.
 * All observable members are bridged from their signal counterparts via `toObservable`
 * to support both signal-based and RxJS-based consumers.
 *
 * ## Naming Convention
 * - `get*` — returns a **Signal**. Use in signal-based or standalone components that
 *   consume reactive state directly via the Angular signals graph.
 * - `select*` — returns an **Observable**. Use in RxJS pipelines, legacy components,
 *   or anywhere that requires interop with the existing reactive stream infrastructure.
 */
@Injectable({ providedIn: 'root' })
export class MarketplaceV2Query {
  private marketplaceStore = inject(MarketplaceItemStore);
  private injector = inject(Injector);
  public readonly isLoaded = this.marketplaceStore.isLoaded;
  public readonly isLoading = this.marketplaceStore.isLoading;

  getMarketplaceList(options: EntityOptions = {}) {
    return this.marketplaceStore.getMarketplaceItems(options);
  }

  /** Returns a signal resolving to the marketplace item matching the given ID, or `undefined` if not found. */
  getMarketplaceById(): Signal<MarketplaceItemV2 | null> {
    return this.marketplaceStore.selectedItem;
  }

  /** Returns a signal resolving to all marketplace items whose `ProcessProviderId` matches one of the provided types. */
  getMarketplaceItemsOfType(types: ProcessProviderId[], options: EntityOptions = {}): Signal<MarketplaceItemV2[]> {
    return this.marketplaceStore.getItemsOfType(types, options);
  }

  /** Returns an observable that emits the marketplace item matching the given ID, or `undefined` if not found. */
  selectMarketplaceById(): Observable<MarketplaceItemV2 | null> {
    return toObservable(this.getMarketplaceById(), { injector: this.injector });
  }

  selectFavourites(): Observable<MarketplaceItemV2[]> {
    return toObservable(this.marketplaceStore.getAll({ filterBy: (item: MarketplaceItemV2) => item.Favourite }), {
      injector: this.injector,
    });
  }

  selectMarketplaceItemsOfType(types: ProcessProviderId[], options: EntityOptions = {}): Observable<MarketplaceItemV2[]> {
    return toObservable(this.getMarketplaceItemsOfType(types, options), { injector: this.injector });
  }

  /**
   * Derives the routing context for a marketplace item based on its `ProcessProviderId`.
   * Returns the back-navigation link, singular type label, and plural group label
   * used to contextualise the item within the correct reward category.
   */
  resolveItemTypeContext(marketplaceItem: MarketplaceItemV2): { backLink: string; type: string; group: string } {
    switch (marketplaceItem?.ProcessProviderId) {
      case ProcessProviderId.Event:
      case ProcessProviderId.VirtualEvent:
        return {
          backLink: '/rewards/events',
          type: 'Event',
          group: 'Events',
        };
      case ProcessProviderId.Product:
        return {
          backLink: '/rewards/products',
          type: 'Product',
          group: 'Products',
        };
      default:
        return {
          backLink: '/rewards/marketplace',
          type: 'Marketplace',
          group: 'Marketplace',
        };
    }
  }
}
